import { faCalendarDay, faChevronRight, faClipboardList } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppointmentListItem } from '@spike/appointments-list-model';
import { RootState } from 'store';
import SidePanel from 'components/UI/SidePanel';
import { DateRangePickerDropdown } from 'components/UI/V3/DateRangePickerDropdown';
import { SelectDropdown } from 'components/UI/V3/SelectDropdown/SelectDropdown';
import { allAllTab, allTabs, getBackendTabsByTabId } from 'components/Bookings2/List/tabs';
import { bookedAtSorter } from 'components/Bookings2/List/sorting';
import { SortOrder } from '@spike/appointments-list-model';
import { useDateRangeOptions } from 'hooks/useDateRangeOptions';
import useTimeZone from 'hooks/useTimeZone';
import { Period } from '@spike/model';
import { fetchThunk } from '@spike/appointments-list-action';
import { AppointmentsListStatus } from '@spike/appointments-list-action';
import { useApiClientWrapper } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import useNonInitialEffect from '@versiondos/hooks';
import Spinner from 'components/UI/V3/Spinner';
import styles from './AppointmentHistoryDrawer.module.scss';
import { status as appointmentStatus } from '@spike/appointment-model';
import moment from 'moment-timezone';

const FETCH_DATE_FORMAT = 'YYYY/MM/DD';

export interface AppointmentHistoryDrawerProps {
    open: boolean;
    onBack: () => void;
    customerId: number;
    petId?: number;
    onAppointmentClick?: (appointment: AppointmentListItem) => void;
    zIndex?: number;
}

export const AppointmentHistoryDrawer: React.FC<AppointmentHistoryDrawerProps> = ({
    open,
    onBack,
    customerId,
    petId,
    onAppointmentClick,
    zIndex
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const { organizationMarketplaces } = useOrganizationMarketplaces();
    const timeZone = useTimeZone();
    const { todayOption, thisMonthOption, last30DaysOption, lastMonthOption, allTimeOption } = useDateRangeOptions();

    const [datePeriod, setDatePeriod] = useState<Period | null>(null);
    const [selectedTabId, setSelectedTabId] = React.useState<string>(allAllTab.id);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (selectedTabId === 'all_cancelled') {
            setSelectedTabId(allAllTab.id);
        }
    }, [selectedTabId]);

    const list = useSelector<RootState, Array<AppointmentListItem>>(state => state.appointmentsList.all.list);
    const status = useSelector<RootState, AppointmentsListStatus>(state => state.appointmentsList.status);

    useEffect(() => {
        if (!open || !customerId) return;
        const marketplaceIds = organizationMarketplaces.map(m => m.id);
        const from = datePeriod?.from ? datePeriod.from.format(FETCH_DATE_FORMAT) : null;
        const to = datePeriod?.to ? datePeriod.to.format(FETCH_DATE_FORMAT) : null;
        const statusIds = [
            appointmentStatus.BOOKED,
            appointmentStatus.CONFIRMED,
            appointmentStatus.CHECKED_IN,
            appointmentStatus.IN_PROGRESS,
            appointmentStatus.READY_FOR_CHECK_OUT,
            appointmentStatus.PAYMENT,
            appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
            appointmentStatus.COMPLETED
        ];

        setIsLoading(true);
        dispatch(
            fetchThunk(
                apiClientWrapper,
                'all',
                allAllTab.serviceCategory,
                marketplaceIds,
                selectedTabId,
                from,
                to,
                null,
                statusIds,
                [],
                getBackendTabsByTabId(selectedTabId),
                [],
                customerId,
                petId ?? null
            )
        );
    }, [open, customerId, petId, selectedTabId, datePeriod?.from?.valueOf(), datePeriod?.to?.valueOf()]);

    useNonInitialEffect(() => {
        switch (status) {
            case AppointmentsListStatus.AllFetching:
                setIsLoading(true);
                break;
            case AppointmentsListStatus.AllFetchSuccess:
            case AppointmentsListStatus.AllError:
                setIsLoading(false);
                break;
        }
    }, [status]);

    const sortedList = useMemo(() => {
        if (!list?.length) return [];
        const now = moment.tz(timeZone);
        const notInFuture = list.filter(app => !app.startAt.isAfter(now));
        return bookedAtSorter.sort([...notInFuture], SortOrder.DESCENDING);
    }, [list, timeZone]);

    const statusFilterOptions = useMemo(
        () => allTabs.filter(tab => tab.id !== 'all_cancelled').map(tab => ({ id: tab.id, label: tab.name })),
        []
    );

    const selectedStatusOption = useMemo(() => {
        const tab = allTabs.find(t => t.id === selectedTabId) ?? allTabs[allTabs.length - 1];
        return { id: tab.id, label: `${tab.name} (${sortedList.length})` };
    }, [selectedTabId, sortedList.length]);

    if (!open) {
        return null;
    }

    return (
        <SidePanel
            side="right"
            hide={false}
            onClose={onBack}
            hideBackdrop={true}
            className={clsx(styles.drawerRoot)}
            classesInner={{
                paper: clsx(styles.offsetPaper, styles.noShadow, styles.borderSeparatorRight)
            }}
            classesBackdrop={{
                classes: {
                    root: styles.drawerBackdrop
                }
            }}
            zIndex={zIndex ? zIndex + 1 : undefined}
        >
            <div className={styles.activityHistoryDrawerRoot}>
                <div className={styles.activityHistoryHeader}>
                    <span className={styles.activityHistoryTitle}>Appointment History</span>
                    <div className={styles.activityHistoryBackButtonContainer}>
                        <FontAwesomeIcon
                            icon={faChevronRight}
                            size="sm"
                            onClick={onBack}
                            className={styles.activityHistoryBackButton}
                        />
                    </div>
                </div>
                <div className={styles.activityHistoryContent}>
                    <div className={styles.activityHistoryFilters}>
                        <SelectDropdown
                            label="Status"
                            options={statusFilterOptions}
                            value={selectedStatusOption}
                            onChange={option => option && setSelectedTabId(option.id)}
                            buttonProps={{
                                size: 'large',
                                leftIcon: faClipboardList,
                                variant: 'outlined'
                            }}
                            dropdownProps={{
                                className: styles.activityHistoryDropdown,
                                mobileBottomModalProps: {
                                    backdropClassName: styles.activityHistoryDropdownBackdrop
                                }
                            }}
                        />
                        <DateRangePickerDropdown
                            timeZone={timeZone}
                            value={datePeriod}
                            onChange={setDatePeriod}
                            options={[todayOption, thisMonthOption, last30DaysOption, lastMonthOption, allTimeOption]}
                            buttonProps={{
                                size: 'large',
                                leftIcon: faCalendarDay,
                                variant: 'outlined'
                            }}
                            dropdownProps={{
                                className: styles.activityHistoryDropdown,
                                mobileBottomModalProps: {
                                    backdropClassName: styles.activityHistoryDropdownBackdrop
                                }
                            }}
                        />
                    </div>
                    {isLoading ? (
                        <div className={styles.centeredContent}>
                            <Spinner />
                        </div>
                    ) : sortedList.length === 0 ? (
                        <div className={styles.centeredContent}>
                            <div className={styles.emptyMessage}>No appointments found</div>
                        </div>
                    ) : (
                        sortedList.map(app => {
                            const cardContent = (
                                <>
                                    <div className={styles.appointmentCardLeft}>
                                        <span className={styles.appointmentServiceName}>{app.service.name}</span>
                                        <span className={styles.appointmentMeta}>
                                            {app.startAt.format('MMM D, YYYY')}
                                            <span> · </span>
                                            {app.staff?.name ?? 'Unassigned'}
                                        </span>
                                    </div>
                                    <span className={styles.appointmentPrice}>${Number(app.total).toFixed(2)}</span>
                                </>
                            );
                            return onAppointmentClick ? (
                                <button
                                    key={app.appointmentId}
                                    type="button"
                                    className={clsx(styles.appointmentCard, styles.appointmentCardButton)}
                                    onClick={() => onAppointmentClick(app)}
                                >
                                    {cardContent}
                                </button>
                            ) : (
                                <div
                                    key={app.appointmentId}
                                    className={styles.appointmentCard}
                                >
                                    {cardContent}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </SidePanel>
    );
};

export default AppointmentHistoryDrawer;
